/**
 * Canonical FX defaults and supported ISO-style codes for the app profile.
 *
 * **Stored `conversionRates[c]` (foreign key `c`, user `displayCurrency` = D):**
 * `rates[c]` = how many **units of `c`** equal **one unit of D**.
 * Amount in `c` converted to D: `amountInC / rates[c]`.
 *
 * Example: `D = ARS`, `rates.USD = 0.001` -> 0.001 USD per 1 ARS -> 10 USD -> 10_000 ARS.
 *
 * The profile form (`DisplaySettingsCard`) shows “1 USD = X ARS” and saves `rates.USD = 1/X`.
 *
 * **Keep in sync with:** `server/api/lib/currencyDefaults.js`
 *
 * Server also exposes defaults + supported list at `GET /users/currency-config` for future dynamic UI.
 */
export const SUPPORTED_APP_CURRENCIES = ["USD", "ARS", "EUR"] as const;

export type AppCurrencyCode = (typeof SUPPORTED_APP_CURRENCIES)[number];
export type ConversionRatesMode = "auto" | "manual";

export const DEFAULT_DISPLAY_CURRENCY: AppCurrencyCode = "ARS";
export const DEFAULT_CONVERSION_RATES_MODE: ConversionRatesMode = "auto";
export const DEFAULT_USD_ARS_REFERENCE = 1000;
export const DEFAULT_EUR_USD_REFERENCE = 1.1;

export type CurrencyReferenceValuesInArs = Record<AppCurrencyCode, number>;

const REFERENCE_VALUE_IN_ARS: CurrencyReferenceValuesInArs = {
    ARS: 1,
    USD: DEFAULT_USD_ARS_REFERENCE,
    EUR: DEFAULT_USD_ARS_REFERENCE * DEFAULT_EUR_USD_REFERENCE,
};

export function isConversionRatesMode(value: unknown): value is ConversionRatesMode {
    return value === "auto" || value === "manual";
}

export function buildReferenceValuesInArs(usdArs?: number): CurrencyReferenceValuesInArs {
    const usdReference = Number.isFinite(usdArs) && Number(usdArs) > 0
        ? Number(usdArs)
        : DEFAULT_USD_ARS_REFERENCE;
    return {
        ARS: 1,
        USD: usdReference,
        EUR: usdReference * DEFAULT_EUR_USD_REFERENCE,
    };
}

export function defaultConversionRatesForDisplay(
    displayCurrency: string,
    referenceValues: CurrencyReferenceValuesInArs = REFERENCE_VALUE_IN_ARS,
): Record<string, number> {
    const normalizedDisplay = String(displayCurrency ?? "").trim().toUpperCase();
    const display = isSupportedAppCurrency(normalizedDisplay) ? normalizedDisplay : DEFAULT_DISPLAY_CURRENCY;
    const displayReference = referenceValues[display] ?? REFERENCE_VALUE_IN_ARS[display];
    const rates: Record<string, number> = {};

    for (const currency of SUPPORTED_APP_CURRENCIES) {
        if (currency === display) continue;
        rates[currency] = displayReference / (referenceValues[currency] ?? REFERENCE_VALUE_IN_ARS[currency]);
    }

    return rates;
}

export function autoConversionRatesForDisplay(displayCurrency: string, usdArs?: number): Record<string, number> {
    return defaultConversionRatesForDisplay(displayCurrency, buildReferenceValuesInArs(usdArs));
}

/** Default units of each foreign currency per 1 unit of `DEFAULT_DISPLAY_CURRENCY`. */
export const DEFAULT_CONVERSION_RATES: Record<string, number> =
    defaultConversionRatesForDisplay(DEFAULT_DISPLAY_CURRENCY);

/** Bounds for persisted manual rates (server and client). */
export const FX_RATE_MIN = 1e-6;
export const FX_RATE_MAX = 1e9;

export function isSupportedAppCurrency(code: string): code is AppCurrencyCode {
    const u = String(code ?? "")
        .trim()
        .toUpperCase();
    return (SUPPORTED_APP_CURRENCIES as readonly string[]).includes(u);
}
